import React, { useState, useEffect, useRef } from 'react';
import { FileText, Save, Info, Sparkles, Bold, Underline, List, ListOrdered, Code, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { DownloadCloud, X } from 'lucide-react';
import { generateAgreementPDF } from '../../utils/pdfGenerator';
import heenaSigImg from '../../assets/heena_prasad_signature.png';

const MoUManagement = () => {
    const [templates, setTemplates] = useState([]);
    const [selectedRole, setSelectedRole] = useState('doctor');
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isVisualMode, setIsVisualMode] = useState(true);
    
    // Offline Generator State
    const [showOfflineModal, setShowOfflineModal] = useState(false);
    const [offlineData, setOfflineData] = useState({
        fullName: '',
        registrationNumber: '',
        hospitalName: '',
        address: '',
        designation: '',
        signatureImg: null
    });

    const editorRef = useRef(null);
    const textareaRef = useRef(null);

    // Sync state to editor div in visual mode
    useEffect(() => {
        if (isVisualMode && editorRef.current) {
            if (editorRef.current.innerHTML !== content) {
                editorRef.current.innerHTML = content;
            }
        }
    }, [content, isVisualMode]);

    const handleVisualInput = () => {
        if (editorRef.current) {
            setContent(editorRef.current.innerHTML);
        }
    };

    const executeCommand = (command, value = null) => {
        if (!isVisualMode) return;
        if (editorRef.current) {
            editorRef.current.focus();
            document.execCommand(command, false, value);
            setContent(editorRef.current.innerHTML);
        }
    };

    const insertPlaceholder = (placeholder) => {
        if (!isVisualMode) {
            // Textarea insertion logic
            const textarea = textareaRef.current;
            if (textarea) {
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const text = textarea.value;
                const newContent = text.substring(0, start) + placeholder + text.substring(end);
                setContent(newContent);
                setTimeout(() => {
                    textarea.focus();
                    textarea.selectionStart = textarea.selectionEnd = start + placeholder.length;
                }, 0);
            }
            return;
        }

        // Visual editor insertion logic
        if (editorRef.current) {
            editorRef.current.focus();
            const selection = window.getSelection();
            if (selection.rangeCount) {
                const range = selection.getRangeAt(0);
                if (editorRef.current.contains(range.commonAncestorContainer)) {
                    range.deleteContents();
                    const textNode = document.createTextNode(placeholder);
                    range.insertNode(textNode);

                    // Move cursor after placeholder
                    range.setStartAfter(textNode);
                    range.setEndAfter(textNode);
                    selection.removeAllRanges();
                    selection.addRange(range);

                    setContent(editorRef.current.innerHTML);
                    return;
                }
            }

            // Fallback (append to end)
            const newContent = content + placeholder;
            setContent(newContent);
            editorRef.current.innerHTML = newContent;
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const response = await api.get('/mou/templates');
            if (response.data?.success) {
                const data = response.data.data || [];
                setTemplates(data);

                // Initialize form with default role (doctor)
                const docTemplate = data.find(t => t.role === 'doctor');
                if (docTemplate) {
                    setTitle(docTemplate.title || '');
                    setContent(docTemplate.content || '');
                }
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to fetch MoU templates');
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = (role) => {
        setSelectedRole(role);
        const selected = templates.find(t => t.role === role);
        if (selected) {
            setTitle(selected.title || '');
            setContent(selected.content || '');
        } else {
            setTitle('');
            setContent('');
        }
    };

    const handleSave = async () => {
        if (!title.trim() || !content.trim()) {
            toast.error('Title and Content are required fields');
            return;
        }

        setSaving(true);
        try {
            const response = await api.put(`/mou/templates/${selectedRole}`, {
                title,
                content
            });
            if (response.data?.success) {
                toast.success('MoU Template saved successfully');
                // Update local state
                setTemplates(prev => {
                    const idx = prev.findIndex(t => t.role === selectedRole);
                    if (idx !== -1) {
                        const updated = [...prev];
                        updated[idx] = response.data.data;
                        return updated;
                    }
                    return [...prev, response.data.data];
                });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handleGenerateOfflinePDF = () => {
        const template = templates.find(t => t.role === selectedRole) || { title, content };
        
        try {
            const doc = generateAgreementPDF(
                selectedRole, 
                offlineData, 
                offlineData.signatureImg, // Partner signature from upload
                heenaSigImg, 
                template
            );
            doc.save(`MoU_Offline_${selectedRole}_${Date.now()}.pdf`);
            toast.success('Offline MoU downloaded successfully!');
            setShowOfflineModal(false);
        } catch (error) {
            console.error(error);
            toast.error('Failed to generate PDF');
        }
    };

    // Helper placeholders to display
    const getPlaceholders = () => {
        switch (selectedRole) {
            case 'doctor':
                return [
                    { tag: '{dateStr}', desc: 'Execution date (e.g., 25 May 2026)' },
                    { tag: '{docName}', desc: 'Full Name of the Doctor (with Dr. prefix)' },
                    { tag: '{regNum}', desc: 'Professional Medical Registration Number' },
                    { tag: '{address}', desc: 'Professional practice address details' }
                ];
            case 'hospital':
                return [
                    { tag: '{dateStr}', desc: 'Execution date (e.g., 25 May 2026)' },
                    { tag: '{hospitalName}', desc: 'Name of the Hospital / Medical Center' },
                    { tag: '{address}', desc: 'Registered hospital address' }
                ];
            case 'marketing_agent':
                return [
                    { tag: '{dateStr}', desc: 'Execution date (e.g., 25 May 2026)' },
                    { tag: '{partnerName}', desc: 'Full Name of the Outreach/BD/Lead Partner' },
                    { tag: '{address}', desc: 'Partner home or office address' }
                ];
            case 'patient':
                return [
                    { tag: '{dateStr}', desc: 'Execution / Acceptance date (e.g., 26 August 2026)' },
                    { tag: '{patientName}', desc: 'Full Name of the Patient' },
                    { tag: '{patientPhone}', desc: 'Contact phone number' },
                    { tag: '{patientEmail}', desc: 'Email address of the patient' },
                    { tag: '{address}', desc: 'Patient address / location' }
                ];
            default:
                return [];
        }
    };

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            {/* Header section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                        <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-[#143E78]">MoU & Agreements</h1>
                        <p className="text-sm font-bold text-gray-500">Manage the legal contracts that partners must sign when joining the platform.</p>
                    </div>
                </div>
                <button
                    onClick={() => setShowOfflineModal(true)}
                    className="bg-[#143E78] hover:bg-blue-900 text-white px-5 py-2.5 rounded-2xl text-xs font-black tracking-widest uppercase shadow-lg shadow-blue-100 hover:shadow-xl transition-all flex items-center justify-center gap-2 shrink-0"
                >
                    <DownloadCloud size={16} /> Generate Offline MoU
                </button>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-pulse">
                    {/* Left Column: Editor Form Skeleton */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white rounded-[32px] p-6 sm:p-8 border border-slate-100 shadow-sm space-y-6">
                            {/* Role Select Placeholder */}
                            <div className="space-y-2">
                                <div className="h-3 w-32 bg-slate-200 rounded"></div>
                                <div className="h-12 w-full bg-slate-50 rounded-2xl"></div>
                            </div>
                            {/* Template Title Placeholder */}
                            <div className="space-y-2">
                                <div className="h-3 w-40 bg-slate-200 rounded"></div>
                                <div className="h-12 w-full bg-slate-50 rounded-2xl"></div>
                            </div>
                            {/* Editor Body Placeholder */}
                            <div className="space-y-2">
                                <div className="h-3 w-36 bg-slate-200 rounded"></div>
                                <div className="border border-slate-100 rounded-2xl overflow-hidden">
                                    <div className="h-12 bg-slate-50 border-b border-slate-100"></div>
                                    <div className="h-[480px] bg-slate-50/50"></div>
                                </div>
                            </div>
                            {/* Save Button Placeholder */}
                            <div className="flex justify-end pt-2">
                                <div className="h-12 w-36 bg-slate-200 rounded-2xl"></div>
                            </div>
                        </div>
                    </div>
                    {/* Right Column: Placeholders Helper Panel Skeleton */}
                    <div className="space-y-6">
                        <div className="bg-slate-50 border border-slate-100 rounded-[32px] p-6 sm:p-8 space-y-6">
                            <div className="h-6 w-48 bg-slate-200 rounded"></div>
                            <div className="h-12 w-full bg-slate-100 rounded"></div>
                            <div className="space-y-3">
                                {[1, 2, 3, 4].map(i => (
                                    <div key={i} className="h-16 w-full bg-slate-100 rounded-2xl"></div>
                                ))}
                            </div>
                            <div className="h-20 w-full bg-slate-100 rounded-2xl"></div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column: Editor Form */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white rounded-[32px] p-6 sm:p-8 shadow-sm border border-gray-100 space-y-6">
                            {/* Role Select */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Select Agreement Target</label>
                                <select
                                    value={selectedRole}
                                    onChange={(e) => handleRoleChange(e.target.value)}
                                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-2xl text-sm font-bold text-[#143E78] outline-none focus:border-blue-500 transition-all cursor-pointer"
                                >
                                    <option value="doctor">Doctor Agreement (Empanelled Medical Professional)</option>
                                    <option value="hospital">Hospital Memorandum of Understanding (MOU)</option>
                                    <option value="marketing_agent">Lead Partner / Outreach Lead Agreement</option>
                                    <option value="patient">Patient Terms & Conditions (Patient Service Agreement)</option>
                                </select>
                            </div>

                            {/* Template Title */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Agreement Document Title</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Enter document title..."
                                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-2xl text-sm font-bold text-[#143E78] outline-none focus:border-blue-500 transition-all"
                                />
                            </div>

                            {/* Template Content */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Agreement Body Content</label>
                                <div className="border-2 border-gray-100 rounded-2xl overflow-hidden focus-within:border-blue-500 transition-all shadow-inner">
                                    {/* Toolbar */}
                                    <div className="bg-gray-50 border-b-2 border-gray-100 p-2 sm:p-3 flex flex-wrap gap-1.5 items-center justify-between">
                                        <div className="flex flex-wrap gap-1 items-center">
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); executeCommand('bold'); }}
                                                disabled={!isVisualMode}
                                                className={`p-2 rounded-lg hover:bg-gray-200 text-[#143E78] transition-all ${!isVisualMode ? 'opacity-30 cursor-not-allowed' : 'active:bg-gray-300'}`}
                                                title="Bold"
                                            >
                                                <Bold size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); executeCommand('underline'); }}
                                                disabled={!isVisualMode}
                                                className={`p-2 rounded-lg hover:bg-gray-200 text-[#143E78] transition-all ${!isVisualMode ? 'opacity-30 cursor-not-allowed' : 'active:bg-gray-300'}`}
                                                title="Underline"
                                            >
                                                <Underline size={16} />
                                            </button>

                                            <div className="w-[1.5px] h-5 bg-gray-200 mx-1.5" />

                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); executeCommand('formatBlock', '<h4>'); }}
                                                disabled={!isVisualMode}
                                                className={`px-2.5 py-1 text-xs font-black rounded-lg hover:bg-gray-200 text-[#143E78] transition-all ${!isVisualMode ? 'opacity-30 cursor-not-allowed' : 'active:bg-gray-300'}`}
                                                title="Heading 4"
                                            >
                                                H4
                                            </button>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); executeCommand('formatBlock', '<h5>'); }}
                                                disabled={!isVisualMode}
                                                className={`px-2.5 py-1 text-xs font-black rounded-lg hover:bg-gray-200 text-[#143E78] transition-all ${!isVisualMode ? 'opacity-30 cursor-not-allowed' : 'active:bg-gray-300'}`}
                                                title="Heading 5"
                                            >
                                                H5
                                            </button>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); executeCommand('formatBlock', '<p>'); }}
                                                disabled={!isVisualMode}
                                                className={`px-2.5 py-1 text-xs font-black rounded-lg hover:bg-gray-200 text-[#143E78] transition-all ${!isVisualMode ? 'opacity-30 cursor-not-allowed' : 'active:bg-gray-300'}`}
                                                title="Normal Paragraph"
                                            >
                                                P
                                            </button>

                                            <div className="w-[1.5px] h-5 bg-gray-200 mx-1.5" />

                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); executeCommand('insertUnorderedList'); }}
                                                disabled={!isVisualMode}
                                                className={`p-2 rounded-lg hover:bg-gray-200 text-[#143E78] transition-all ${!isVisualMode ? 'opacity-30 cursor-not-allowed' : 'active:bg-gray-300'}`}
                                                title="Bullet List"
                                            >
                                                <List size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); executeCommand('insertOrderedList'); }}
                                                disabled={!isVisualMode}
                                                className={`p-2 rounded-lg hover:bg-gray-200 text-[#143E78] transition-all ${!isVisualMode ? 'opacity-30 cursor-not-allowed' : 'active:bg-gray-300'}`}
                                                title="Numbered List"
                                            >
                                                <ListOrdered size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); executeCommand('removeFormat'); }}
                                                disabled={!isVisualMode}
                                                className={`px-2 py-1 text-[11px] font-black rounded-lg hover:bg-gray-200 text-[#143E78] transition-all ${!isVisualMode ? 'opacity-30 cursor-not-allowed' : 'active:bg-gray-300'}`}
                                                title="Clear Formatting"
                                            >
                                                Clear
                                            </button>
                                        </div>

                                        {/* Toggle visual/code mode */}
                                        <button
                                            type="button"
                                            onClick={() => setIsVisualMode(!isVisualMode)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#143E78]/10 hover:bg-[#143E78]/25 text-[#143E78] rounded-xl text-xs font-bold transition-all active:scale-[0.97]"
                                        >
                                            {isVisualMode ? (
                                                <>
                                                    <Code size={14} />
                                                    <span>HTML Source</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Eye size={14} />
                                                    <span>Visual Editor</span>
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {/* Editor Canvas */}
                                    <div className="bg-white">
                                        <style>{`
                                            .mou-editor-canvas {
                                                outline: none;
                                            }
                                            .mou-editor-canvas h4 {
                                                font-size: 1.125rem;
                                                font-weight: 700;
                                                color: #143E78;
                                                margin-top: 1rem;
                                                margin-bottom: 0.5rem;
                                            }
                                            .mou-editor-canvas h5 {
                                                font-size: 1rem;
                                                font-weight: 700;
                                                color: #143E78;
                                                margin-top: 0.75rem;
                                                margin-bottom: 0.25rem;
                                            }
                                            .mou-editor-canvas p {
                                                font-size: 0.875rem;
                                                line-height: 1.6;
                                                color: #334155;
                                                margin-top: 0.5rem;
                                                margin-bottom: 0.75rem;
                                            }
                                            .mou-editor-canvas ul {
                                                list-style-type: disc;
                                                padding-left: 1.5rem;
                                                margin-top: 0.5rem;
                                                margin-bottom: 0.75rem;
                                            }
                                            .mou-editor-canvas ol {
                                                list-style-type: decimal;
                                                padding-left: 1.5rem;
                                                margin-top: 0.5rem;
                                                margin-bottom: 0.75rem;
                                            }
                                            .mou-editor-canvas li {
                                                font-size: 0.875rem;
                                                color: #334155;
                                                margin-bottom: 0.25rem;
                                            }
                                            .mou-editor-canvas strong {
                                                font-weight: 700;
                                            }
                                            .mou-editor-canvas u {
                                                text-decoration: underline;
                                            }
                                        `}</style>
                                        {isVisualMode ? (
                                            <div
                                                ref={editorRef}
                                                contentEditable
                                                onInput={handleVisualInput}
                                                onBlur={handleVisualInput}
                                                className="w-full min-h-[480px] max-h-[600px] px-5 py-4 overflow-y-auto text-[#143E78] mou-editor-canvas text-justify"
                                            />
                                        ) : (
                                            <textarea
                                                ref={textareaRef}
                                                value={content}
                                                onChange={(e) => setContent(e.target.value)}
                                                placeholder="Enter agreement body content using HTML tags like <p>, <h4>, <h5>..."
                                                className="w-full min-h-[480px] max-h-[600px] px-5 py-4 text-sm font-semibold text-[#143E78] outline-none font-mono focus:bg-gray-50/50 transition-all border-0 text-justify"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Action Button */}
                            <div className="flex justify-end pt-2">
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="bg-[#143E78] hover:bg-blue-900 text-white px-8 py-3 rounded-2xl text-xs font-black tracking-widest uppercase shadow-lg shadow-blue-100 hover:shadow-xl transition-all disabled:opacity-50 flex items-center gap-2"
                                >
                                    <Save size={16} /> {saving ? 'Saving...' : 'Save Template'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Placeholders Helper Panel */}
                    <div className="space-y-6">
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-[32px] p-6 sm:p-8 space-y-6">
                            <div className="flex items-center gap-2 text-[#143E78]">
                                <Sparkles className="w-5 h-5" />
                                <h3 className="font-black text-lg">Dynamic Placeholders</h3>
                            </div>

                            <p className="text-xs font-semibold text-slate-600 leading-relaxed">
                                Use these exact placeholders in your agreement content. The system will replace them automatically during onboarding.
                            </p>

                            <div className="space-y-3">
                                {getPlaceholders().map((p) => (
                                    <button
                                        key={p.tag}
                                        type="button"
                                        onMouseDown={(e) => { e.preventDefault(); insertPlaceholder(p.tag); }}
                                        className="w-full text-left bg-white/85 hover:bg-blue-50/60 border border-white hover:border-blue-200 p-3.5 rounded-2xl flex flex-col gap-1 shadow-sm hover:shadow-md transition-all active:scale-[0.99] group cursor-pointer"
                                        title="Click to insert at cursor position"
                                    >
                                        <div className="flex items-center justify-between w-full">
                                            <code className="text-xs font-bold text-blue-600 font-mono select-all">
                                                {p.tag}
                                            </code>
                                            <span className="text-[9px] font-bold text-blue-500 bg-blue-50/80 px-2 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                                                Click to Insert
                                            </span>
                                        </div>
                                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tighter">
                                            {p.desc}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 flex gap-3 text-[#143E78]">
                                <Info className="w-5 h-5 shrink-0 mt-0.5" />
                                <div className="text-[11px] font-bold leading-relaxed space-y-1">
                                    <p>Tip: You can use HTML tags:</p>
                                    <ul className="list-disc pl-4 space-y-0.5">
                                        <li><code>&lt;p&gt;</code> for regular paragraphs</li>
                                        <li><code>&lt;h4&gt;</code> / <code>&lt;h5&gt;</code> for headings</li>
                                        <li><code>&lt;strong&gt;</code> for <strong>bold text</strong></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Offline MoU Generator Modal */}
            {showOfflineModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-[32px] w-full max-w-lg overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="bg-gradient-to-br from-[#143E78] to-blue-900 p-6 flex justify-between items-center text-white">
                            <div className="flex items-center gap-3">
                                <DownloadCloud className="w-6 h-6 text-blue-200" />
                                <h3 className="font-black text-lg">Generate Offline MoU</h3>
                            </div>
                            <button onClick={() => setShowOfflineModal(false)} className="text-blue-200 hover:text-white transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 sm:p-8 space-y-5">
                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Target Role</label>
                                    <select 
                                        value={selectedRole} 
                                        onChange={(e) => handleRoleChange(e.target.value)}
                                        className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-2xl text-sm font-bold text-[#143E78] outline-none focus:border-blue-500 transition-all cursor-pointer"
                                    >
                                        <option value="doctor">Doctor</option>
                                        <option value="hospital">Hospital</option>
                                        <option value="marketing_agent">Marketing Partner</option>
                                        <option value="patient">Patient</option>
                                    </select>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Full Name (Doctor/Partner)</label>
                                    <input type="text" value={offlineData.fullName} onChange={e => setOfflineData({...offlineData, fullName: e.target.value})} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-2xl text-sm font-bold text-[#143E78] outline-none focus:border-blue-500 transition-all" placeholder="Dr. John Doe" />
                                </div>

                                {selectedRole === 'doctor' && (
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Registration Number</label>
                                        <input type="text" value={offlineData.registrationNumber} onChange={e => setOfflineData({...offlineData, registrationNumber: e.target.value})} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-2xl text-sm font-bold text-[#143E78] outline-none focus:border-blue-500 transition-all" placeholder="e.g. 12345 (Leave blank for hand-written fill)" />
                                    </div>
                                )}

                                {selectedRole === 'hospital' && (
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Hospital Name</label>
                                        <input type="text" value={offlineData.hospitalName} onChange={e => setOfflineData({...offlineData, hospitalName: e.target.value})} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-2xl text-sm font-bold text-[#143E78] outline-none focus:border-blue-500 transition-all" placeholder="City General Hospital" />
                                    </div>
                                )}
                                
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Designation</label>
                                    <input type="text" value={offlineData.designation} onChange={e => setOfflineData({...offlineData, designation: e.target.value})} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-2xl text-sm font-bold text-[#143E78] outline-none focus:border-blue-500 transition-all" placeholder={selectedRole === 'doctor' ? 'Medical Specialist' : selectedRole === 'hospital' ? 'Authorized Representative' : selectedRole === 'patient' ? 'Patient / Beneficiary' : 'Partner'} />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Full Address</label>
                                    <textarea value={offlineData.address} onChange={e => setOfflineData({...offlineData, address: e.target.value})} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-100 rounded-2xl text-sm font-bold text-[#143E78] outline-none focus:border-blue-500 transition-all min-h-[80px]" placeholder="123 Health Ave, Mumbai, 400001" />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Partner Signature (Optional Image)</label>
                                    <input 
                                        type="file" 
                                        accept="image/png, image/jpeg, image/jpg"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    setOfflineData({...offlineData, signatureImg: reader.result});
                                                };
                                                reader.readAsDataURL(file);
                                            } else {
                                                setOfflineData({...offlineData, signatureImg: null});
                                            }
                                        }}
                                        className="w-full px-4 py-2 bg-gray-50 border-2 border-gray-100 rounded-2xl text-xs font-bold text-[#143E78] outline-none focus:border-blue-500 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-black file:bg-[#143E78] file:text-white hover:file:bg-blue-900 cursor-pointer" 
                                    />
                                    {offlineData.signatureImg && (
                                        <div className="mt-2 flex items-center justify-between bg-blue-50 p-2 rounded-xl border border-blue-100">
                                            <span className="text-[10px] font-bold text-blue-600 uppercase">Signature Attached</span>
                                            <button onClick={() => setOfflineData({...offlineData, signatureImg: null})} className="text-red-500 hover:text-red-700">
                                                <X size={14} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button onClick={() => setShowOfflineModal(false)} className="flex-1 py-3 px-4 rounded-2xl font-black text-gray-500 hover:bg-gray-100 transition-colors uppercase tracking-widest text-xs">
                                    Cancel
                                </button>
                                <button onClick={handleGenerateOfflinePDF} className="flex-1 bg-[#143E78] hover:bg-blue-900 text-white py-3 px-4 rounded-2xl font-black tracking-widest uppercase text-xs shadow-lg shadow-blue-100 hover:shadow-xl transition-all flex items-center justify-center gap-2">
                                    <DownloadCloud size={16} /> Generate PDF
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MoUManagement;
